import type { SqlPort, Statement } from '../shared'

/**
 * The row index of the stored log, written once when a transaction is published, so a pull never decodes stored
 * bytes (db-crdt-sync.spec.md "Pull", :uc[pull-serves-rows-from-admission-index]; requirements 2026-11-02 "On pull the
 * server decodes nothing"). Prior art: PowerSync buckets and Electric shapes assign rows at write time.
 *
 * A stored frame is a canonical protobuf message (turso sync/row-scope/src/wire.rs `TransactionFrame`; the fork's
 * `decode_frame` requires `encode_to_vec() == bytes`), so its data rows (field 5) form one contiguous run between the
 * header fields (1-4) and `database_version` (6). Removing row entries from that run gives the same bytes as the fork's
 * `filter_transaction` (lib.rs, decode, retain, encode) without interpreting any row.
 *
 * Per row: its binding (table, key, scope, operation), the private `owner` (a row with an owner is readable by that
 * owner only; '' for a row without one), the shareable `resource`, `via` (a resource whose readers also read the row)
 * and its byte span. A pull reads rows only through the owner index, which covers every column it reads: each row it
 * serves is read once and never a row of another owner (D1 rows_read, private-pull-cost.d1.test.ts). WITHOUT ROWID
 * would cluster the same way, but the Turso engine the lib also runs on refuses it without an experimental flag.
 * Per revision: the skeleton (the frame without its rows, the placeholder of a revision with nothing readable), where
 * its rows start, and how many it has. A revision without a skeleton was never indexed: a pull page ends before it
 * (transaction-log.ts scopedPage) until it is indexed, so no cursor passes it.
 */
export type RowBinding = { table: string; key: string; scope: string; operation: number; owner?: string; resource?: string; via?: string }

// Contiguous coverage, not MAX(skeleton.revision): newer indexed publications can follow an old Worker's gap.
// Maintaining it at publication/backfill time keeps pull work independent of indexed private history.
const advanceCoverage = (workspace: string) => `UPDATE _turso_transaction_row_checkpoint SET revision=COALESCE(
  (SELECT l.revision-1 FROM _turso_transaction_log l WHERE l.workspace=${workspace} AND l.revision>_turso_transaction_row_checkpoint.revision AND l.catalog_digest IS NULL
    AND NOT EXISTS(SELECT 1 FROM _turso_transaction_skeletons s WHERE s.workspace=l.workspace AND s.revision=l.revision) ORDER BY l.revision LIMIT 1),
  (SELECT MAX(revision) FROM _turso_transaction_log WHERE workspace=${workspace}),0) WHERE workspace=${workspace}`

export const transactionRowCheckpointDDL = [
  'CREATE TABLE IF NOT EXISTS _turso_transaction_row_checkpoint(workspace TEXT PRIMARY KEY,revision INTEGER NOT NULL)',
  `INSERT INTO _turso_transaction_row_checkpoint(workspace,revision)
    SELECT l.workspace,COALESCE((SELECT missing.revision-1 FROM _turso_transaction_log missing WHERE missing.workspace=l.workspace AND missing.catalog_digest IS NULL
      AND NOT EXISTS(SELECT 1 FROM _turso_transaction_skeletons s WHERE s.workspace=missing.workspace AND s.revision=missing.revision) ORDER BY missing.revision LIMIT 1),MAX(l.revision))
    FROM _turso_transaction_log l WHERE NOT EXISTS(SELECT 1 FROM _turso_transaction_row_checkpoint c WHERE c.workspace=l.workspace) GROUP BY l.workspace`,
  `CREATE TRIGGER IF NOT EXISTS _turso_transaction_row_checkpoint_log AFTER INSERT ON _turso_transaction_log BEGIN
    INSERT INTO _turso_transaction_row_checkpoint(workspace,revision) VALUES(NEW.workspace,0) ON CONFLICT DO NOTHING;
    ${advanceCoverage('NEW.workspace')}; END`,
  `CREATE TRIGGER IF NOT EXISTS _turso_transaction_row_checkpoint_index AFTER INSERT ON _turso_transaction_skeletons BEGIN ${advanceCoverage('NEW.workspace')}; END`,
  `CREATE TRIGGER IF NOT EXISTS _turso_transaction_row_checkpoint_delete AFTER DELETE ON _turso_transaction_skeletons BEGIN
    UPDATE _turso_transaction_row_checkpoint SET revision=MIN(revision,OLD.revision-1) WHERE workspace=OLD.workspace; END`,
]

export const transactionRowsDDL = [
  `CREATE TABLE IF NOT EXISTS _turso_transaction_rows(workspace TEXT NOT NULL,revision INTEGER NOT NULL,idx INTEGER NOT NULL,owner TEXT NOT NULL,table_name TEXT NOT NULL,row_key TEXT NOT NULL,scope TEXT NOT NULL,resource TEXT,via TEXT,operation INTEGER NOT NULL,span_start INTEGER NOT NULL,span_end INTEGER NOT NULL,PRIMARY KEY(workspace,revision,idx))`,
  // A pull reads its caller's rows and the rows without an owner by key range, no other owner's
  // (:uc[private-pull-sends-only-callers-rows]); the index holds every column the pull reads (covering).
  'CREATE INDEX IF NOT EXISTS _turso_transaction_rows_owner ON _turso_transaction_rows(workspace,owner,revision,span_start,span_end,table_name,operation,resource,via,row_key)',
  'CREATE INDEX IF NOT EXISTS _turso_transaction_rows_identity ON _turso_transaction_rows(workspace,table_name,row_key,revision)',
  'CREATE TABLE IF NOT EXISTS _turso_transaction_skeletons(workspace TEXT NOT NULL,revision INTEGER NOT NULL,skeleton BLOB NOT NULL,rows_at INTEGER NOT NULL,row_count INTEGER NOT NULL,PRIMARY KEY(workspace,revision))',
  ...transactionRowCheckpointDDL,
]

const rowsField = 5

/** Byte spans of the frame's data rows (field 5), in row order, and its skeleton (the frame without them). */
export function frameRows(bytes: Uint8Array): { spans: { start: number; end: number }[]; rowsAt: number; skeleton: Uint8Array } {
  const spans: { start: number; end: number }[] = []
  let offset = 0
  const varint = () => {
    let value = 0
    for (let shift = 0; shift < 64; shift += 7) {
      const byte = bytes[offset++]
      if (byte === undefined) throw new Error('Truncated transaction frame')
      value += (byte & 127) * 2 ** shift
      if (byte < 128) return value
    }
    throw new Error('Invalid transaction frame varint')
  }
  let rowsAt: number | undefined
  while (offset < bytes.length) {
    const start = offset
    const key = varint()
    const wire = key % 8
    if (wire === 0) varint()
    else if (wire === 1) offset += 8
    else if (wire === 5) offset += 4
    else if (wire === 2) { const length = varint(); offset += length }
    else throw new Error('Unsupported transaction frame wire type')
    if (offset > bytes.length) throw new Error('Truncated transaction frame')
    if (Math.floor(key / 8) !== rowsField) continue
    if (wire !== 2) throw new Error('Invalid transaction frame row')
    rowsAt ??= start
    if (spans.length > 0 && spans.at(-1)?.end !== start) throw new Error('Transaction frame rows are not contiguous')
    spans.push({ start, end: offset })
  }
  const at = rowsAt ?? bytes.length
  const end = spans.at(-1)?.end ?? at
  const skeleton = new Uint8Array(bytes.length - (end - at))
  skeleton.set(bytes.subarray(0, at))
  skeleton.set(bytes.subarray(end), at)
  return { spans, rowsAt: at, skeleton }
}

/**
 * Statements indexing one published frame: its skeleton and one row per binding (one binding per frame row, in row
 * order). `where` selects the frame's log entry (columns of `_turso_transaction_log`), so the index commits only with
 * its publication, in the same batch.
 */
export function transactionRowsStatements(workspace: string, bindings: readonly RowBinding[], bytes: Uint8Array, where: Statement): Statement[] {
  const { spans, rowsAt, skeleton } = frameRows(bytes)
  if (spans.length !== bindings.length) throw new Error(`Row bindings (${bindings.length}) differ from frame rows (${spans.length})`)
  const statements: Statement[] = [{
    sql: `INSERT INTO _turso_transaction_skeletons(workspace,revision,skeleton,rows_at,row_count)
      SELECT workspace,revision,?,?,? FROM _turso_transaction_log WHERE workspace=? AND ${where.sql} ON CONFLICT DO NOTHING`,
    params: [skeleton, rowsAt, bindings.length, workspace, ...where.params ?? []],
  }]
  for (let start = 0; start < bindings.length; start += 8) {
    const rows = bindings.slice(start, start + 8).map((binding, offset) => {
      const idx = start + offset
      return [idx, binding.table, binding.key, binding.scope, binding.owner ?? '', binding.resource ?? null, binding.via ?? null, binding.operation, spans[idx].start, spans[idx].end]
    })
    statements.push({
    sql: `INSERT INTO _turso_transaction_rows(workspace,revision,idx,table_name,row_key,scope,owner,resource,via,operation,span_start,span_end)
      SELECT l.workspace,l.revision,json_extract(r.value,'$[0]'),json_extract(r.value,'$[1]'),json_extract(r.value,'$[2]'),json_extract(r.value,'$[3]'),json_extract(r.value,'$[4]'),json_extract(r.value,'$[5]'),json_extract(r.value,'$[6]'),json_extract(r.value,'$[7]'),json_extract(r.value,'$[8]'),json_extract(r.value,'$[9]')
      FROM _turso_transaction_log l CROSS JOIN json_each(?) r WHERE l.workspace=? AND ${where.sql} ON CONFLICT DO NOTHING`,
    params: [JSON.stringify(rows), workspace, ...where.params ?? []],
  })
  }
  return statements
}

/**
 * Statements re-indexing a revision the freeze rewrote in place (:uc[freeze-keeps-row-index-equal-to-stored-bytes]):
 * `kept` lists, in order, the positions (among the revision's current rows) that survive in `bytes`; the other rows
 * leave the index and every kept row takes its span from the rewritten bytes and its new position as `idx`.
 */
export function reindexedRowsStatements(workspace: string, revision: number, kept: readonly number[], bytes: Uint8Array, guard: Statement): Statement[] {
  const { spans } = frameRows(bytes)
  if (spans.length !== kept.length) throw new Error(`Rewritten rows (${spans.length}) differ from kept rows (${kept.length})`)
  const moves = kept.map((position, idx) => [position, idx, spans[idx].start, spans[idx].end])
  const params = guard.params ?? []
  return [
    // Kept rows move to negative positions first, so renumbering never meets a primary key still held by another row.
    { sql: `UPDATE _turso_transaction_rows SET idx=-1-idx WHERE workspace=? AND revision=? AND idx>=0 AND ${guard.sql}`, params: [workspace, revision, ...params] },
    { sql: `UPDATE _turso_transaction_rows SET idx=json_extract(m.value,'$[1]'),span_start=json_extract(m.value,'$[2]'),span_end=json_extract(m.value,'$[3]')
      FROM json_each(?) m WHERE workspace=? AND revision=? AND idx=-1-json_extract(m.value,'$[0]') AND ${guard.sql}`, params: [JSON.stringify(moves), workspace, revision, ...params] },
    { sql: `DELETE FROM _turso_transaction_rows WHERE workspace=? AND revision=? AND idx<0 AND ${guard.sql}`, params: [workspace, revision, ...params] },
    { sql: `UPDATE _turso_transaction_skeletons SET row_count=? WHERE workspace=? AND revision=? AND ${guard.sql}`, params: [kept.length, workspace, revision, ...params] },
  ]
}

/** The stored frame with only the rows at `kept` spans, in frame order; no row is interpreted. */
export function framedRows(bytes: Uint8Array, skeleton: Uint8Array, rowsAt: number, kept: readonly (readonly [number, number])[]): Uint8Array {
  const ordered = kept.toSorted((left, right) => left[0] - right[0])
  const size = ordered.reduce((total, [start, end]) => total + end - start, 0)
  const framed = new Uint8Array(skeleton.length + size)
  framed.set(skeleton.subarray(0, rowsAt))
  let offset = rowsAt
  for (const [start, end] of ordered) { framed.set(bytes.subarray(start, end), offset); offset += end - start }
  framed.set(skeleton.subarray(rowsAt), offset)
  return framed
}

/** Workspaces with a revision that has no index yet (the same predicate as `unindexedRevisions`). */
export async function* unindexedWorkspaces(sql: SqlPort, canRead: () => boolean = () => true): AsyncGenerator<string> {
  let after: string | null = null
  while (canRead()) {
    const [row]: { workspace: string }[] = await sql.query<{ workspace: string }>(`SELECT DISTINCT l.workspace FROM _turso_transaction_log l WHERE l.catalog_digest IS NULL
      AND (? IS NULL OR l.workspace>?)
      AND NOT EXISTS(SELECT 1 FROM _turso_transaction_skeletons s WHERE s.workspace=l.workspace AND s.revision=l.revision) ORDER BY l.workspace LIMIT 1`, [after, after])
    if (!row) return
    after = row.workspace
    yield row.workspace
  }
}

/** Revisions of the workspace with no index yet (published before the index existed), oldest first; catalog frames pass whole and need none. */
export async function unindexedRevisions(sql: SqlPort, workspace: string, limit: number): Promise<number[]> {
  const rows = await sql.query<{ revision: number }>(`SELECT l.revision FROM _turso_transaction_log l WHERE l.workspace=? AND l.catalog_digest IS NULL
    AND NOT EXISTS(SELECT 1 FROM _turso_transaction_skeletons s WHERE s.workspace=l.workspace AND s.revision=l.revision) ORDER BY l.revision LIMIT ?`, [workspace, limit])
  return rows.map(row => row.revision)
}

/** Retained device revisions for identities changed in this window; publication bases are loaded separately. */
export function retainedBaseRevisions(sql: SqlPort, workspace: string, floor: number, through: number) {
  return sql.query<{ revision: number }>(`SELECT DISTINCT base.revision
    FROM (SELECT DISTINCT table_name,row_key FROM _turso_transaction_rows
      WHERE workspace=? AND revision>? AND revision<=?) changed
    CROSS JOIN _turso_transaction_rows base INDEXED BY _turso_transaction_rows_identity
    WHERE base.workspace=? AND base.table_name=changed.table_name AND base.row_key=changed.row_key
      AND base.revision<=? ORDER BY base.revision`, [workspace, floor, through, workspace, floor])
}
